import React, { useState, useEffect } from 'react';
import { Calendar, User, Tag, ArrowLeft, Clock } from 'lucide-react';

const ArticlePage = ({ slug }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError('Article slug is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const url = `https://newsletter.manuelabamert.com/ghost/api/content/posts/slug/${slug}/`;
        const params = new URLSearchParams({
          key: "d02d48a81aa0765c8cb29cce8d",
          include: "authors,tags",
          filter: "visibility:public",
          formats: "html,plaintext"
        });

        const response = await fetch(`${url}?${params}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Article not found');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setArticle(data.posts[0] || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBackToBlog = () => {
    // This would typically use React Router's navigate or similar
    // For now, we'll use window.history or provide a callback
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // Fallback to blog homepage
      window.location.href = '/blog';
    }
  };

  const handleTagClick = (tagSlug) => {
    // Navigate to blog page with tag filter
    window.location.href = `/blog?tag=${encodeURIComponent(tagSlug)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error === 'Article not found' ? 'Article Not Found' : 'Error Loading Article'}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBackToBlog}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Article not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBackToBlog}
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </button>

        {/* Article Header */}
        <article className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Feature Image */}
          {article.feature_image && (
            <div className="w-full h-64 md:h-96">
              <img 
                src={article.feature_image} 
                alt={article.feature_image_alt || article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="p-6 md:p-8">
            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleTagClick(tag.slug)}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors duration-200 cursor-pointer"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {article.title}
            </h1>

            {/* Custom Excerpt */}
            {article.custom_excerpt && (
              <div className="text-xl text-gray-600 mb-6 font-light leading-relaxed">
                {article.custom_excerpt}
              </div>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap items-center text-sm text-gray-500 mb-8 gap-4 pb-6 border-b border-gray-200">
              {/* Author */}
              {article.primary_author && (
                <div className="flex items-center">
                  {article.primary_author.profile_image ? (
                    <img 
                      src={article.primary_author.profile_image} 
                      alt={article.primary_author.name}
                      className="w-8 h-8 rounded-full mr-2"
                    />
                  ) : (
                    <User className="w-4 h-4 mr-2" />
                  )}
                  <span className="font-medium">{article.primary_author.name}</span>
                </div>
              )}

              {/* Published Date */}
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{formatDate(article.published_at)}</span>
              </div>

              {/* Reading Time */}
              {article.reading_time > 0 && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{article.reading_time} min read</span>
                </div>
              )}

              {/* Updated Date (if different from published) */}
              {article.updated_at !== article.published_at && (
                <div className="text-xs text-gray-400">
                  Updated {formatDate(article.updated_at)}
                </div>
              )}
            </div>

            {/* Article HTML Content */}
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-img:rounded-lg prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: article.html }}
            />

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              {/* Author Bio */}
              {article.primary_author && article.primary_author.bio && (
                <div className="flex items-start space-x-4 mb-6">
                  {article.primary_author.profile_image ? (
                    <img 
                      src={article.primary_author.profile_image} 
                      alt={article.primary_author.name}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      About {article.primary_author.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {article.primary_author.bio}
                    </p>
                    {article.primary_author.website && (
                      <a 
                        href={article.primary_author.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm hover:underline"
                      >
                        Visit website
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Related Tags */}
              {article.tags && article.tags.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => handleTagClick(tag.slug)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>

        {/* Back to Blog Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleBackToBlog}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Articles
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;